// fx.jsx - refined motion primitives. Minimal. No spring, no scramble.

const { useEffect, useRef, useState, useCallback } = React;

// Shared smoothed scroll - single rAF loop feeds all subscribers.
const scrollStore = (() => {
  let raw = 0, smooth = 0, running = false;
  const subs = new Set();
  const tick = () => {
    raw = window.scrollY || window.pageYOffset || 0;
    smooth += (raw - smooth) * 0.08; // slower lerp = more cinematic
    subs.forEach((fn) => fn(smooth, raw));
    if (Math.abs(raw - smooth) > 0.05 || subs.size > 0) requestAnimationFrame(tick);
    else running = false;
  };
  const start = () => { if (!running) { running = true; requestAnimationFrame(tick); } };
  return { subscribe(fn) { subs.add(fn); start(); return () => subs.delete(fn); } };
})();

function useScrollRef(onTick) {
  useEffect(() => scrollStore.subscribe(onTick), [onTick]);
}

function useInView(ref, opts = {}) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => setInView(e.isIntersecting)),
      { threshold: 0.15, ...opts }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return inView;
}

// Progress of an element as it passes through the viewport, 0..1.
// Used for pinned scroll sequences.
function useScrollProgress(ref) {
  const [p, setP] = useState(0);
  useScrollRef(useCallback(() => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight;
    const total = r.height + vh;
    const traveled = vh - r.top;
    setP(Math.max(0, Math.min(1, traveled / total)));
  }, [ref]));
  return p;
}

// Reveal - long, calm ease. No overshoot.
// When window.__SSR_SNAPSHOT__ is set (Puppeteer prerender), skip the
// intersection-observer gate and render at final state immediately so the
// snapshot captures visible content instead of opacity:0 pre-animation frames.
function Reveal({ children, delay = 0, distance = 20, once = true, className, style }) {
  const ref = useRef(null);
  const inView = useInView(ref, { threshold: 0.12 });
  const [seen, setSeen] = useState(!!(window.__SSR_SNAPSHOT__));
  useEffect(() => { if (inView) setSeen(true); }, [inView]);
  const show = once ? seen : inView;
  return (
    <div ref={ref} className={className}
      style={{
        transform: show ? 'translate3d(0,0,0)' : `translate3d(0,${distance}px,0)`,
        opacity: show ? 1 : 0,
        transition: `transform 1.2s cubic-bezier(.16,.77,.28,.99) ${delay}ms, opacity 1s ease ${delay}ms`,
        willChange: 'transform, opacity',
        ...style,
      }}>
      {children}
    </div>
  );
}

// WordCycle - each word mounts, animates in, stays, animates out, unmounts.
// Two spans coexist briefly during transition. Deterministic, no stuck-state.
function WordCycle({ words, interval = 1800, duration = 700, style, className, wordStyle }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % words.length), interval);
    return () => clearInterval(t);
  }, [words.length, interval]);

  const widest = words.reduce((a, b) => (a.length > b.length ? a : b));
  return (
    <span style={{ position: 'relative', display: 'inline-block', verticalAlign: 'bottom', clipPath: 'inset(0 -999px)', ...style }} className={className}>
      {/* width reservation, also carries the wordStyle so layout matches */}
      <span aria-hidden style={{ visibility: 'hidden', display: 'inline-block', whiteSpace: 'nowrap', ...wordStyle }}>{widest}</span>
      <span aria-live="polite" style={{ position: 'absolute', inset: 0, display: 'inline-flex', alignItems: 'baseline' }}>
        <span
          key={idx}
          style={{
            display: 'inline-block',
            whiteSpace: 'nowrap',
            animation: `wc-cycle ${interval}ms cubic-bezier(.22,.9,.3,1) both`,
            ...wordStyle,
          }}>
          {words[idx]}
        </span>
      </span>
      <style>{`
        @keyframes wc-cycle {
          0%   { transform: translateY(110%); opacity: 0; filter: blur(8px); }
          18%  { transform: translateY(0);    opacity: 1; filter: blur(0); }
          82%  { transform: translateY(0);    opacity: 1; filter: blur(0); }
          100% { transform: translateY(-110%); opacity: 0; filter: blur(8px); }
        }
      `}</style>
    </span>
  );
}

Object.assign(window, { Reveal, WordCycle, useScrollRef, useScrollProgress, useInView });
